import {
  useExternalStoreRuntime,
  useExternalMessageConverter,
  type AppendMessage,
  type CompleteAttachment,
  type ThreadMessageLike,
  useAuiState,
  useAui,
} from '@assistant-ui/react';
import { useNotifications } from '@nohara-dxdev/react-core';
import { useScopeIds } from '@nohara-dxdev/react-oidc';
import { useQueryClient } from '@tanstack/react-query';
import { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { getMessagesQueryKey, useMessagesInfinite } from '../api/list-messages';
import { MessageTooLongError, MonthlyTokenLimitError, sendMessage } from '../api/send-message';
import { useFileAttachments } from '../hooks/use-file-attachments';
import { useStreamingMessage } from '../hooks/use-streaming-message';

import { createAttachmentAdapter } from './attachment.adapter';
import { convertMessages } from './thread-history.adapter';

import type { MessagesPage } from '../types';

export function useAiExternalStoreRuntime() {
  const { orgId, projectId } = useScopeIds('org', 'project');
  const queryClient = useQueryClient();
  const streaming = useStreamingMessage();
  const fileAttachments = useFileAttachments();
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const locale: 'en' | 'ja' = language.startsWith('en') ? 'en' : 'ja';

  const notifications = useNotifications();
  const aui = useAui();
  const threadListItem = useAuiState((s) => s.threadListItem);

  // MT1-1430: surface AI usage-limit rejections as a toast; returns true when handled.
  const handleUsageLimitError = useCallback(
    (error: unknown): boolean => {
      if (error instanceof MonthlyTokenLimitError) {
        notifications.error(t('aiChat.error.monthlyTokenLimitReached'));
        return true;
      }
      if (error instanceof MessageTooLongError) {
        notifications.error(t('aiChat.error.messageTooLong', { maxLength: error.maxLength ?? '' }));
        return true;
      }
      return false;
    },
    [notifications, t],
  );
  const sessionId = threadListItem.remoteId ?? null;

  const [isRunning, setIsRunning] = useState(false);

  // Load history messages for active session
  const messagesQuery = useMessagesInfinite({
    orgId,
    projectId,
    sessionId: sessionId ?? '',
  });
  const historyMessages = useMemo(() => {
    const data = messagesQuery.data as any;
    if (!data?.pages) return [];
    const allItems = data.pages.flatMap((p: MessagesPage) => p.messages);
    return convertMessages(allItems);
  }, [messagesQuery.data]);

  // Optimistic user message stored in state (useState triggers re-render correctly)
  const [optimisticMsg, setOptimisticMsg] = useState<
    (ThreadMessageLike & { externalId?: string }) | null
  >(null);

  // Derive streaming message as ThreadMessageLike
  const streamingMsgLike = useMemo(
    (): ThreadMessageLike | null =>
      streaming.message
        ? {
            id: streaming.messageId ?? 'streaming',
            role: 'assistant' as const,
            content: streaming.message.content,
            createdAt: streaming.message.createdAt,
          }
        : null,
    [streaming.message, streaming.messageId],
  );

  // Synthetic system message that surfaces a feature-disabled stream frame inline.
  const errorMsgLike = useMemo<ThreadMessageLike | null>(() => {
    if (streaming.error !== '[FEATURE_DISABLED]') return null;
    return {
      id: 'feature-disabled-error',
      role: 'system' as const,
      content: [{ type: 'text', text: t('featureFlags.aiAgent.streamDisabled') }],
      createdAt: new Date(),
    };
  }, [streaming.error, t]);

  // Combine messages: history + optimistic (if not yet in history) + streaming
  const combinedMessages = useMemo((): ThreadMessageLike[] => {
    const result = [...historyMessages];

    // Include optimistic message only if its ID is NOT yet in history
    if (optimisticMsg && !historyMessages.some((m) => m.externalId === optimisticMsg.id)) {
      result.push(optimisticMsg);
    }

    // Include streaming message while it exists — even after isStreaming=false,
    // keep it until history contains a message with the same ID (prevents flicker)
    if (streamingMsgLike && !historyMessages.some((m) => m.id === streamingMsgLike.id)) {
      result.push(streamingMsgLike);
    }

    if (errorMsgLike) {
      result.push(errorMsgLike);
    }

    return result;
  }, [historyMessages, optimisticMsg, streamingMsgLike, errorMsgLike]);

  // Use assistant-ui's converter — identity callback since combinedMessages is already ThreadMessageLike[]
  const messages = useExternalMessageConverter<ThreadMessageLike>({
    callback: (msg) => msg,
    messages: combinedMessages,
    isRunning: isRunning,
    joinStrategy: 'none',
  });

  const onNew = useCallback(
    async (appendMessage: AppendMessage) => {
      const textContent = appendMessage.content
        .filter((p): p is { type: 'text'; text: string } => p.type === 'text')
        .map((p) => p.text)
        .join('');

      // Optimistic user message (D-17) — stored in state, included by useMemo above
      // eslint-disable-next-line n/no-unsupported-features/node-builtins
      const externalId = crypto.randomUUID();
      setOptimisticMsg({
        id: externalId,
        externalId,
        role: 'user' as const,
        content: appendMessage.content,
        createdAt: new Date(),
      });
      setIsRunning(true);

      try {
        let resolvedSessionId = sessionId;
        if (!resolvedSessionId) {
          const { remoteId } = await aui.threadListItem().initialize();
          resolvedSessionId = remoteId;
        }

        const attachmentKeys = (appendMessage.attachments ?? [])
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          .filter((a): a is CompleteAttachment => a.status.type === 'complete')
          .map((a) => ({
            key:
              // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
              a.content?.[0]?.type === 'text'
                ? (a.content[0] as { type: 'text'; text: string }).text
                : '',
            contentType: a.contentType ?? 'application/octet-stream',
          }))
          .filter((a) => a.key);

        const debug = new URLSearchParams(window.location.search).get('debug') === 'true';
        const { streamUrl } = await sendMessage({
          orgId,
          projectId,
          sessionId: resolvedSessionId,
          externalId,
          content: textContent,
          attachments: attachmentKeys.length > 0 ? attachmentKeys : undefined,
          debug: debug ? true : undefined,
          locale,
        });

        await streaming.startStream(streamUrl);
        void queryClient.invalidateQueries({ queryKey: getMessagesQueryKey(resolvedSessionId) });
        fileAttachments.clearAttachments();
      } catch (error) {
        setOptimisticMsg((current) => (current?.id === externalId ? null : current));
        if (handleUsageLimitError(error)) return;
        throw error;
      } finally {
        setIsRunning(false);
      }
    },
    [
      sessionId,
      orgId,
      projectId,
      streaming,
      fileAttachments,
      locale,
      queryClient,
      aui,
      handleUsageLimitError,
    ],
  );

  const onReload = useCallback(
    async (parentId: string | null) => {
      if (!sessionId) return;
      // D-08: Find the user message that preceded the assistant message being regenerated.
      // parentId = the assistant message ID to regenerate from.
      // Find the index of that assistant message, then walk backwards for the nearest user message.
      let userMsg: (typeof messages)[number] | undefined;
      if (parentId) {
        const asstIdx = messages.findIndex((m) => m.id === parentId);
        if (asstIdx > 0) {
          // Walk backwards from the assistant message to find the preceding user message
          for (let k = asstIdx - 1; k >= 0; k--) {
            if (messages[k].role === 'user') {
              userMsg = messages[k];
              break;
            }
          }
        }
      }
      // Fallback: if parentId is null (first message) or assistant not found, use last user message
      // Note: Array.findLast requires ES2023 lib — use manual reverse-iteration for ES2020 compat
      if (!userMsg) {
        for (let k = messages.length - 1; k >= 0; k--) {
          if (messages[k].role === 'user') {
            userMsg = messages[k];
            break;
          }
        }
      }
      if (!userMsg) return;
      const text = userMsg.content
        .filter((p): p is { type: 'text'; text: string } => p.type === 'text')
        .map((p) => p.text)
        .join('');

      setIsRunning(true);
      try {
        const debug = new URLSearchParams(window.location.search).get('debug') === 'true';
        const { streamUrl } = await sendMessage({
          orgId,
          projectId,
          sessionId,
          content: text,
          attachments: undefined,
          debug: debug ? true : undefined,
          parentId: parentId ?? undefined,
          locale,
        });

        await streaming.startStream(streamUrl);

        void queryClient.invalidateQueries({ queryKey: getMessagesQueryKey(sessionId) });
      } catch (error) {
        if (handleUsageLimitError(error)) return;
        throw error;
      } finally {
        setIsRunning(false);
      }
    },
    [sessionId, orgId, projectId, messages, queryClient, streaming, locale, handleUsageLimitError],
  );

  const onCancel = useCallback(() => {
    streaming.stopStream();
    return Promise.resolve();
  }, [streaming]);

  const attachmentAdapter = useMemo(
    () => createAttachmentAdapter({ orgId, projectId, sessionId: sessionId ?? '' }),
    [orgId, projectId, sessionId],
  );

  return useExternalStoreRuntime({
    messages,
    isRunning: isRunning,
    onNew,
    onReload,
    onCancel,
    adapters: {
      attachments: attachmentAdapter,
    },
  });
}
